import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { buildAttachmentDownloadUrl } from '@/features/patient-record/components/DocumentsSection/attachmentUrl';
import { patientQueries } from '@/features/patient-record/queries';
import { downloadBlob } from '@/lib/downloadFile';

interface QuestionnaireAttachmentsListProps {
  readonly guid: string;
  readonly requestGuid: string;
}

/**
 * The submission artifacts (PDF + answers JSON) stored against a questionnaire request, fetched by
 * `targetId = requestGuid`. Mounted lazily (only when a row's inline disclosure is open), so the
 * query is enabled here rather than in the shared query factory.
 */
export function QuestionnaireAttachmentsList({
  guid,
  requestGuid,
}: QuestionnaireAttachmentsListProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery({
    ...patientQueries.questionnaireAttachments(guid, requestGuid),
    enabled: true,
  });

  if (query.isPending) {
    return (
      <p className="text-xs text-muted-foreground">
        {t('patient.questionnaires.attachments.loading')}
      </p>
    );
  }
  if (query.isError) {
    return (
      <p className="text-xs text-destructive">{t('patient.questionnaires.attachments.error')}</p>
    );
  }

  const items = query.data;
  if (items.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">{t('patient.questionnaires.attachments.empty')}</p>
    );
  }

  return (
    <ul className="space-y-0.5">
      {items.map((attachment) => {
        const name = attachment.fileName ?? attachment.id;
        return (
          <li key={attachment.id}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-full justify-start gap-1.5 px-2 text-xs"
              onClick={() => {
                void downloadBlob(buildAttachmentDownloadUrl(attachment.id), name);
              }}
            >
              <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-left">{name}</span>
              <Download className="h-3 w-3 shrink-0 opacity-60" aria-hidden="true" />
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
